import React, { useState, useEffect, useRef } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import { useAuth0 } from '@auth0/auth0-react';
import { apiCallErrorHandler } from '../utils/common';
import './QueueForm.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const VIN_REGEX = /([A-HJ-NPR-Z0-9]{17})/gi;

const parseVinsFromText = (input) => {
  if (!input) return [];
  const matches = Array.from(input.matchAll(VIN_REGEX), (m) => m[1].toUpperCase());
  return Array.from(new Set(matches));
};

export default function VinListEnrichmentForm({ source = null }) {
  const toast = useRef(null);
  const menuRef = useRef(null);
  const { getAccessTokenSilently } = useAuth0();
  useEffect(() => { document.title = 'OptiConfig VIN List Enrichment'; }, []);

  const [text, setText] = useState('');
  const [parsedVins, setParsedVins] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const prevParsedRef = useRef('');

  useEffect(() => {
    setParsedVins(parseVinsFromText(text));
  }, [text]);

  // generate a new sessionId when parsedVins changes (no API call here)
  useEffect(() => {
    const vinsKey = parsedVins.join('|');
    if (vinsKey === prevParsedRef.current) return;
    prevParsedRef.current = vinsKey;

    if (!parsedVins || parsedVins.length === 0) {
      setSessionId(null);
      return;
    }

    const newSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
    setSessionId(newSessionId);
  }, [parsedVins]);

  const showToast = (severity, summary, detail) => {
    if (toast.current) toast.current.show({ severity, summary, detail, life: 4000 });
  };

  const handleClear = async () => {
    // if there's a server session, discard it
    if (sessionId) {
      if (!window.confirm('Discard the created session on server? This action cannot be undone.')) return;
      setSubmitting(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${API_BASE_URL}/file/discard/${sessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) await apiCallErrorHandler(res, 'Failed to discard session');
        showToast('success', 'Success', 'Session discarded');
      } catch (err) {
        showToast('error', 'Error', err.message || 'Failed to discard session');
      } finally {
        setSubmitting(false);
      }
    }

    // clear local state
    setText('');
    setParsedVins([]);
    prevParsedRef.current = '';
    setSessionId(null);
  };

  // call API when user clicks enrich (uses existing sessionId if present)
  const handleEnrich = async (selectedSource = null) => {
    if (!parsedVins.length) {
      showToast('warn', 'No VINs', 'Please paste VINs to enrich.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const endpoint = sessionId ? `${API_BASE_URL}/file/enrich-list/${sessionId}` : `${API_BASE_URL}/file/enrich-top`;
      const payload = {
        top5: parsedVins.map(vin => {
            return { vin };
        }),
        fileName: "n/a",
        worksheet: "vinlist",
        mapping: { "VIN": "VIN" },
        grouping: [ "VIN" ],
        source: selectedSource ?? source ?? undefined
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) await apiCallErrorHandler(res, 'Enrichment request failed');

      await res.json();
      showToast('success', 'Enrichment started', `Enrichment accepted for ${parsedVins.length} VIN(s)`);
    } catch (err) {
      showToast('error', 'Error', err.message || 'Enrichment request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const enrichItems = [
    { label: 'Enrich with DAT', command: () => handleEnrich('Dat') },
    { label: 'Enrich with Outvin', command: () => handleEnrich('Outvin') },
    { label: 'Enrich with Jato', command: () => handleEnrich('Jato') },
    { label: 'Enrich with Vincario', command: () => handleEnrich('Vincario') },
    { label: 'Enrich with Partslink', command: () => handleEnrich('Partslink') },
    { label: 'Enrich with MyRenault', command: () => handleEnrich('MyRenault') },
    { label: 'Enrich with ChatGPT', command: () => handleEnrich('ChatGpt') },
    { label: 'Enrich with Claude', command: () => handleEnrich('Claude') },
    { label: 'Enrich with Autobiz', command: () => handleEnrich('Autobiz') },
    { label: 'Enrich Supplier Data', command: () => handleEnrich('Supplier') },
  ];

  return (
    <div className="queue-form card" style={{ maxWidth: 900 }}>
      <Toast ref={toast} />
      <h3 style={{ textAlign: 'left', marginBottom: '1rem' }}>VIN List Enrichment</h3>
      <br />
      <div className="queue-split" style={{ alignItems: 'flex-start' }}>
        <div className="queue-split__left" style={{ flex: 1 }}>
          <label className="p-mb-2">Paste VINs</label>
          <InputTextarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            autoResize
            className="w-full"
            placeholder="Paste VINs or text containing VINs (one per line or anywhere in text)"
          />
        </div>

        <div className="queue-split__right" style={{ width: 280 }}>
          <div className="vin-list-header">
            <span>Parsed VINs</span>
            <span className="vin-count">{parsedVins.length}</span>
          </div>
          <div className="vin-list-body" style={{ maxHeight: 320, overflow: 'auto' }}>
            {parsedVins.length === 0 ? (
              <div className="vin-empty">No VINs parsed</div>
            ) : (
              parsedVins.map((v) => (
                <div key={v} className="vin-item">
                  {v}
                </div>
              ))
            )}
          </div>

          <div style={{ marginTop: '1rem', display: 'flex', gap: '.5rem', alignItems: 'center' }}>
            <Button
              label={submitting ? 'Enriching...' : 'Enrich VINS'}
              className="p-button-primary"
              onClick={(e) => menuRef.current && menuRef.current.toggle(e)}
              disabled={parsedVins.length === 0 || submitting}
            />
            <Menu model={enrichItems} popup ref={menuRef} style={{ minWidth: '220px' }} />
            <Button
              label="Clear"
              className="p-button-secondary"
              onClick={handleClear}
              disabled={submitting || (text === '' && parsedVins.length === 0)}
            />
          </div>

        </div>
      </div>
    </div>
  );
}